import {
  Typography,
  Button,
  Hidden,
  IconButton,
  Radio,
  Tooltip,
  Drawer,
  Stack,
} from "@mui/material";
import {
  styled,
  useTheme,
  Theme,
  CSSObject,
  lighten,
  darken,
} from "@mui/material/styles";
import { ChevronRight, MenuRounded } from "@mui/icons-material";
import { MenuItemModel, MenuModel } from "../models/menu.model";
import clsx from "clsx";
import { grey } from "@mui/material/colors";
import React from "react";
import { checkIfHasPermission } from "../models/auth.model";
import { useLocation, useNavigate } from "react-router-dom";
import { titleStr } from "../lib/helper";
import LogoText from "./LogoText";

const ChervonRightIcon = styled(ChevronRight, {
  shouldForwardProp: (prop) => prop !== "expand",
})<{ expand: boolean }>(({ expand }) => ({
  transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  ...(expand && {
    transform: "rotate(90deg)",
  }),
}));

const MenuButton = styled(Button)(({ theme }) => ({
  width: "100%",
  justifyContent: "flex-start",
  textTransform: "initial",
  color: grey[600],
  "& .dot": {
    display: "block",
    width: theme.spacing(0.5),
    height: theme.spacing(0.5),
    backgroundColor: grey[600],
    transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  },
  "&.active": {
    "&.main": {
      color: theme.palette.primary.main,
      backgroundColor: lighten(theme.palette.primary.main, 0.9),
      "&::before": {
        top: 0,
        right: 0,
        width: 3,
        bottom: 0,
        content: '""',
        position: "absolute",
        borderTopLeftRadius: 4,
        borderBottomLeftRadius: 4,
        backgroundColor: darken(theme.palette.primary.main, 0.1),
      },
    },
    "&.sub .title": {
      fontWeight: "bold",
      color: "black",
    },
    "& .dot": {
      width: theme.spacing(1),
      height: theme.spacing(1),
      backgroundColor: darken(theme.palette.primary.main, 0.1),
    },
  },
}));

const MenuTextDiv = styled("div")(() => ({
  maxHeight: 0,
  transition: "max-height 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  overflow: "hidden",
}));

function MenuText(props: {
  menu: MenuItemModel;
  activeLink: string;
  drawerExpand?: boolean;
  isSub?: boolean;
  onNavigate: (p: string) => void;
}) {
  const activeLink = props.isSub
    ? props.activeLink
    : props.activeLink.split("-")[0];
  const [expand, setExpand] = React.useState(activeLink === props.menu.link);
  const menuItems = props.menu.menuItems?.filter((a) =>
    checkIfHasPermission(a.permissions),
  );
  return (
    <div>
      <MenuButton
        onClick={
          props.menu.menuItems
            ? () => setExpand(!expand)
            : () => props.onNavigate(props.menu.link)
        }
        className={clsx(
          "padding-normal padding-l-large",
          { sub: props.isSub },
          { main: !props.isSub },
          {
            active:
              activeLink === props.menu.link ||
              props.activeLink === props.menu.link,
          }
        )}
        data-testid={props.menu.title}
      >
        <div className="between-wrapper width-100p">
          <div className="center-wrapper">
            {props.menu.icon}
            <Typography
              variant="body2"
              className="title capitalize padding-l-normal"
            >
              {titleStr(props.menu.title)}
            </Typography>
          </div>
          {props.menu.menuItems && <ChervonRightIcon expand={expand} />}
        </div>
      </MenuButton>
      <MenuTextDiv
        style={{
          maxHeight:
            props.drawerExpand && expand ? (menuItems?.length || 0) * 48 : 0,
        }}
      >
        {menuItems?.map((item) => (
          <MenuText
            key={item.title}
            isSub
            activeLink={props.activeLink}
            onNavigate={() => props.onNavigate(item.link)}
            menu={item}
          />
        ))}
      </MenuTextDiv>
    </div>
  );
}

function Menu(props: { menus: MenuModel[]; drawerExpand: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div>
      {props.menus.map((m) => {
        const menus = m.menus.filter((me) =>
          checkIfHasPermission(me.permissions),
        );
        return (
          <div key={m.title}>
            {menus.length > 0 && (
              <div>
                <Typography
                  className="padding-normal subtitle uppercase padding-l-large"
                  variant="subtitle2"
                >
                  {titleStr(m.title)}
                </Typography>
              </div>
            )}
            {menus.map((menu) => (
              <MenuText
                key={menu.title}
                drawerExpand={props.drawerExpand}
                menu={menu}
                activeLink={location.pathname}
                onNavigate={(p) => navigate(p)}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

const drawerWidth = 280;

const DrawerNav = styled("nav")(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    flexShrink: 0,
  },
}));

const TempDrawer = styled(Drawer)(() => ({
  width: drawerWidth - 40,
  "& .MuiDrawer-paper": {
    width: drawerWidth - 40,
  },
}));

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

const PermanentDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "expand",
})<{ expand: boolean }>(({ theme, expand }) => ({
  width: drawerWidth,
  flexShrink: 0,
  ...(expand && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!expand && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
  ...(!expand && {
    overflowX: "hidden",
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9) + 1,
    },
    "& .padding-l-large": {
      paddingLeft: theme.spacing(3),
    },
    "& .title": {
      marginLeft: theme.spacing(3),
    },
    "& .subtitle": {
      display: "none",
    },
  }),
}));

const DrawerButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(2),
  [theme.breakpoints.up("lg")]: {
    display: "none",
  },
  top: 13,
  position: "absolute",
}));

const PinRadio = styled(Radio)(({ theme }) => ({
  color: lighten(theme.palette.primary.main, 0.9),
  height: 42,
  "&.Mui-checked": {
    color: darken(theme.palette.primary.main, 0.1),
  },
}));

function ResponsiveDrawer(props: { menus: MenuModel[] }) {
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [expand, setExpand] = React.useState(true);
  const [pinned, setPin] = React.useState(true);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handlePinCheck = () => {
    setPin(!pinned);
    pinned && setExpand(false);
  };

  return (
    <DrawerNav aria-label="mailbox folders" data-testid={"leftPanel"}>
      <Hidden lgUp implementation="css">
        <TempDrawer
          variant="temporary"
          anchor={theme.direction === "rtl" ? "right" : "left"}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
        >
          {<Menu drawerExpand {...props} />}
        </TempDrawer>
      </Hidden>
      <Hidden lgUp>
        <DrawerButton className="padding-l-large" onClick={handleDrawerToggle}>
          <MenuRounded />
        </DrawerButton>
      </Hidden>
      <Hidden lgDown implementation="css">
        <PermanentDrawer
          onMouseEnter={() => setExpand(true)}
          onMouseLeave={() => setExpand(pinned)}
          expand={expand}
          variant="permanent"
          open
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 1, mb: 1, px: 2 }}
            spacing={1}
          >
            <LogoText iconWidth={60} textVariant="h5" />
            <Tooltip title="Pin Menu">
              <PinRadio
                color="default"
                checked={pinned}
                onClick={handlePinCheck}
                value="pinned"
              />
            </Tooltip>
          </Stack>
          {<Menu {...props} drawerExpand={expand} />}
        </PermanentDrawer>
      </Hidden>
    </DrawerNav>
  );
}

export default ResponsiveDrawer;
